/** Service Analytics — Suntronix-only.
 *  Live volumes per outcome group, catalogue totals and real transactional
 *  counts from the actual collections (never fabricated). */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, BarChart3 } from "lucide-react";

function Tile({ label, value, hint, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testId}>
      <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">{label}</div>
      <div className="mt-1 st-metric st-metric-md">{value ?? "—"}</div>
      {hint && <div className="mt-0.5 text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

export default function ServiceAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/services/analytics")
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="max-w-6xl space-y-4"><div className="st-skeleton h-8 w-64" /><div className="grid grid-cols-4 gap-3">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="st-skeleton h-24" />)}</div></div>;
  if (error) return <div className="max-w-3xl bg-white border border-slate-200 rounded-md p-6 text-sm text-slate-700">{error}</div>;

  const maxGroup = Math.max(1, ...data.groups.map((g) => g.count));

  return (
    <div className="max-w-6xl space-y-6" data-testid="services-analytics">
      <div>
        <Link to="/services" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to services
        </Link>
        <div className="mt-3 flex items-baseline gap-3">
          <BarChart3 className="h-5 w-5 text-slate-700" strokeWidth={1.6} />
          <h1 className="font-heading text-[26px] font-semibold text-slate-900 tracking-tight">Service Analytics</h1>
        </div>
        <p className="text-sm text-slate-500 mt-1">Evidence-only. Every number comes from the actual catalogue and transactional collections.</p>
      </div>

      <section>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500 mb-2">Catalogue</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Tile label="Services" value={data.totals.services} hint="Active in catalogue" testId="tile-services" />
          <Tile label="Bundles" value={data.totals.bundles} testId="tile-bundles" />
          <Tile label="Add-ons" value={data.totals.addons} testId="tile-addons" />
          <Tile label="Featured" value={data.totals.featured} hint="Surfaced as Quick Actions" testId="tile-featured" />
          <Tile label="Premium" value={data.totals.premium} testId="tile-premium" />
        </div>
      </section>

      <section>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500 mb-2">Live volumes</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile label="Requests open" value={data.volumes.requests_open} hint={`of ${data.volumes.requests_total} lifetime`} testId="tile-req-open" />
          <Tile label="Service cases open" value={data.volumes.service_cases_open} hint={`of ${data.volumes.service_cases_total} lifetime`} testId="tile-cases-open" />
          <Tile label="Orders open" value={data.volumes.orders_open} hint={`of ${data.volumes.orders_total} lifetime`} testId="tile-orders-open" />
          <Tile label="Shipments in transit" value={data.volumes.shipments_active} testId="tile-ship-active" />
          <Tile label="Pickups in progress" value={data.volumes.pickups_active} testId="tile-pick-active" />
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500 mb-3">Services per outcome group</div>
        <ul className="space-y-2">
          {data.groups.map((g) => (
            <li key={g.key} data-testid={`grp-row-${g.key}`}>
              <Link to={`/services/group/${g.key}`} className="grid grid-cols-[160px_1fr_60px] items-center gap-3 py-1 hover:bg-slate-50 rounded px-2">
                <span className="text-sm text-slate-700">{g.label}</span>
                <span className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <span className="block h-full bg-slate-900" style={{ width: `${Math.round((g.count / maxGroup) * 100)}%` }} />
                </span>
                <span className="text-sm font-mono text-slate-800 text-right">{g.count}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
